import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Sparkles, X } from "lucide-react";
import { SIDEBAR_SECTIONS } from "@shared/sidebar-pages";
import {
  markChangelogSeen,
  useAckWhatsNew,
  useWhatsNew,
  type WhatsNewEntry,
} from "@/hooks/use-updates";

/**
 * #1285 — moshi-style launch announcements.
 *
 * Mounted once in the app shell. When the server reports published changelog
 * entries newer than this user's seen watermark (KV, per-user — see the route
 * for why not localStorage), it surfaces one batched "since you last looked"
 * modal. Every dismissal path acks the watermark: once seen, re-showing the
 * same batch is nagging. Entries arrive already RBAC-filtered server-side, so
 * nothing here decides visibility — it only presents.
 *
 * The repo has no dialog library; like UpdateToast this is a single-purpose,
 * theme-token-styled surface rather than a new dependency.
 */

const CATEGORY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  feature: { bg: "bg-blue-500/10", text: "text-blue-600 dark:text-blue-400", label: "Feature" },
  fix: { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", label: "Fix" },
  improvement: { bg: "bg-amber-500/10", text: "text-amber-600 dark:text-amber-400", label: "Improvement" },
};

const PAGE_LABELS: Record<string, string> = Object.fromEntries(
  SIDEBAR_SECTIONS.flatMap((s) => s.pages)
    .filter((p) => p.path && !p.divider)
    .map((p) => [p.path, p.label]),
);

function EntryCard({ entry, onOpenPage }: { entry: WhatsNewEntry; onOpenPage: (path: string) => void }) {
  const cat = CATEGORY_STYLES[entry.category] ?? CATEGORY_STYLES.feature;
  const pageLabel = entry.page_path ? PAGE_LABELS[entry.page_path] : null;
  return (
    <li className="rounded-lg border border-border bg-background/60 p-4">
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${cat.bg} ${cat.text}`}>
          {cat.label}
        </span>
        <h3 className="flex-1 text-sm font-semibold leading-snug">{entry.title}</h3>
      </div>
      <div className="prose prose-sm mt-1.5 max-w-none text-[13px] leading-relaxed text-muted-foreground dark:prose-invert [&_p]:my-1">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.description}</ReactMarkdown>
      </div>
      {entry.page_path && (
        <button
          type="button"
          onClick={() => onOpenPage(entry.page_path as string)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Open {pageLabel ?? entry.page_path}
          <ArrowRight className="h-3 w-3" aria-hidden />
        </button>
      )}
    </li>
  );
}

export function WhatsNewModal() {
  const { data } = useWhatsNew();
  const ack = useAckWhatsNew();
  const navigate = useNavigate();
  // Once dismissed, stay dismissed for this batch even before the ack
  // round-trip lands. A later publication gets a newer marker and reopens the
  // modal while this AppShell remains mounted.
  const [dismissedThrough, setDismissedThrough] = useState<{ published_at: string; id: number } | null>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);

  const entries = data?.entries ?? [];
  const newestEntry = entries[0];
  const open = Boolean(
    newestEntry &&
      (!dismissedThrough ||
        newestEntry.published_at > dismissedThrough.published_at ||
        (newestEntry.published_at === dismissedThrough.published_at && newestEntry.id > dismissedThrough.id)),
  );

  const dismissBatch = () => {
    if (newestEntry) {
      setDismissedThrough({ published_at: newestEntry.published_at, id: newestEntry.id });
    }
    markChangelogSeen();
    ack.mutate();
  };

  const close = () => {
    dismissBatch();
  };

  useEffect(() => {
    if (!open) return;
    primaryRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const openPage = (path: string) => {
    dismissBatch();
    navigate(path);
  };

  const seeAll = () => {
    dismissBatch();
    navigate("/updates");
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <style>{`
        @keyframes wn-backdrop { from { opacity: 0 } to { opacity: 1 } }
        @keyframes wn-card { from { opacity: 0; transform: translateY(14px) scale(0.98) } to { opacity: 1; transform: none } }
      `}</style>
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        style={{ animation: "wn-backdrop 200ms ease-out" }}
        onClick={() => close()}
        aria-hidden
      />
      <div
        className="relative flex max-h-[min(80vh,42rem)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-2xl"
        style={{ animation: "wn-card 260ms cubic-bezier(0.16, 1, 0.3, 1)" }}
      >
        <div className="flex items-start gap-3 border-b border-border bg-gradient-to-br from-primary/10 via-transparent to-transparent px-5 py-4">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
            <Sparkles className="h-4.5 w-4.5" aria-hidden />
          </span>
          <div className="flex-1">
            <h2 id="whats-new-title" className="text-base font-semibold leading-tight">
              What&apos;s new in Crosbe AI
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {entries.length === 1 ? "1 update" : `${entries.length} updates`} since you last looked
            </p>
          </div>
          <button
            type="button"
            onClick={() => close()}
            aria-label="Dismiss"
            className="rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <ul className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {entries.map((e) => (
            <EntryCard key={e.id} entry={e} onOpenPage={openPage} />
          ))}
        </ul>

        <div className="flex items-center justify-between gap-3 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={seeAll}
            className="text-xs font-medium text-muted-foreground transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            See all updates
          </button>
          <button
            ref={primaryRef}
            type="button"
            onClick={() => close()}
            className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
